import { ProductAlias } from './billing.enums';

/**
 * Catalog product ids. These are database-assigned (`products.id`), so they are
 * NOT guaranteed to be the same number in every environment — a locally seeded
 * catalog already numbers `affiliate` 6 with a gap at 5. The reliable key is the
 * {@link ProductAlias}; a numeric id is only safe to hardcode where a mismatch
 * degrades to a fallback (a title lookup) rather than to a wrong product. When
 * an id has to be sent somewhere (the plans page's `product_id` preselect),
 * resolve it by alias — `CatalogService.getProductIdByAlias`.
 */
export enum ProductId {
	SearchingOutreach = 1,
	PartnerAds = 2,
	SocialListening = 3,
	Affiliate = 6
}

// Product alias is the backend ProductAlias enum (kills magic strings).
export { ProductAlias } from './billing.enums';

export interface Product {
	id: ProductId | number;
	alias?: ProductAlias;
	title: string;
	is_active: boolean;
}

export interface ProductMeta {
	id: ProductId;
	alias: ProductAlias;
	title: string;
}

export const PRODUCT_REGISTRY: Record<ProductId, ProductMeta> = {
	[ProductId.SearchingOutreach]: {
		id: ProductId.SearchingOutreach,
		alias: ProductAlias.DISCOVERY,
		title: 'Discovery'
	},
	[ProductId.PartnerAds]: {
		id: ProductId.PartnerAds,
		alias: ProductAlias.META_ADS,
		title: 'Partner Ads'
	},
	[ProductId.SocialListening]: {
		id: ProductId.SocialListening,
		alias: ProductAlias.SOCIAL_LISTENING,
		title: 'Social Listening'
	},
	// The affiliate department is one product. Registered so the paywall names
	// it ("Affiliates plan required", not "This plan required") and the plans
	// page can preselect it; titled after the department the brand navigates to,
	// not after the catalog row ("Affiliate").
	[ProductId.Affiliate]: {
		id: ProductId.Affiliate,
		alias: ProductAlias.AFFILIATE,
		title: 'Affiliates'
	}
};

/**
 * The products whose plans the brand's plans page SELLS, by alias.
 *
 * WHY THIS LIST EXISTS AND WHY IT IS HERE RATHER THAN IN THE BACKEND READ.
 * `GET /billing/catalog/plans` answers "which plans are subscribable and
 * published" — `is_active AND is_shown`, both facts about a plan — and it is
 * read by more than one surface (this page, the public pricing page, a
 * grandfathered plan looked up by id). Which products belong on THIS page is a
 * fact about this page, so it is scoped here. The endpoint already takes a
 * `product_id`; nothing about it needed to change.
 *
 * What it excludes, and why each one is not an oversight:
 *
 * - `discovery`, `social_listening`, `meta_ads` — superseded by the one
 *   `application` subscription and retired (`is_shown = false` on every plan
 *   they still carry). A brand that holds one keeps it, which is enforced by
 *   reading rather than by selling it again;
 * - `affiliate` — reachable on every tier and priced as a percentage of
 *   attributed sales, so it has no plan to sell.
 *
 * `creator_data_api` stays: it was deliberately NOT folded into the application
 * (its buyer wants credits and a rate rather than the app), it keeps its own
 * ladder, and it is the one other thing this page has ever sold.
 */
/**
 * The `application` product's id IN THE MOCK CATALOGUE only.
 *
 * Not in {@link ProductId}, and deliberately: that enum is documented as ids the
 * database assigns, safe to hardcode only where a mismatch degrades to a
 * fallback. Nothing in the real app resolves the application product by number —
 * plan aliases and `CatalogService.getProductIdByAlias` do that — so a constant
 * here would be a number pretending to be a fact. The mocks need SOME id to key
 * their rows by; this is it, and it lives next to the enum so the two are read
 * together.
 */
export const MOCK_PLATFORM_PRODUCT_ID = 7;

export const PLANS_PAGE_PRODUCTS: ReadonlyArray<ProductAlias> = [
	ProductAlias.APPLICATION,
	ProductAlias.CREATOR_DATA_API
];

export const ALL_PRODUCT_IDS: ProductId[] = [
	ProductId.SearchingOutreach,
	ProductId.PartnerAds,
	ProductId.SocialListening,
	ProductId.Affiliate
];
